"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { Input } from "./input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./select";
import { DocumentUploadField, type UploadedDocumentMetadata } from "./document-upload-field";
import { cn } from "../../lib/utils";
import {
    PAN_DOCUMENT_ACCEPT,
    PAN_DOCUMENT_MAX_SIZE_MB,
    PAYOUT_METHOD,
    INDIA_COUNTRY_CODE,
    type PayoutMethod,
} from "@artha/types";

export interface ManagePayoutFormData {
    method: PayoutMethod;
    country: string;
    address: string;
    account_type?: "savings" | "current";
    beneficiary_name?: string;
    bank_account?: string;
    confirm_bank_account?: string;
    ifsc?: string;
    pan?: string;
    paypal_email?: string;
}

export interface ManagePayoutBottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdate: (data: ManagePayoutFormData) => Promise<void>;
    initialData?: Partial<ManagePayoutFormData>;
    isLoading?: boolean;
    panDocument?: UploadedDocumentMetadata | null;
    onPanDocumentUpload?: (file: File) => void;
    onPanDocumentView?: () => void;
    isPanDocumentUploading?: boolean;
    isPanDocumentViewing?: boolean;
    panDocumentError?: string;
}

const SUPPORTED_COUNTRIES = [
    { code: INDIA_COUNTRY_CODE, name: "India", currency: "INR" },
    { code: "US", name: "United States", currency: "USD" },
    { code: "GB", name: "United Kingdom", currency: "GBP" },
    { code: "CA", name: "Canada", currency: "CAD" },
    { code: "AU", name: "Australia", currency: "AUD" },
    { code: "SG", name: "Singapore", currency: "SGD" },
    { code: "AE", name: "United Arab Emirates", currency: "AED" },
];

export function ManagePayoutBottomSheet({
    isOpen,
    onClose,
    onUpdate,
    initialData,
    isLoading = false,
    panDocument,
    onPanDocumentUpload,
    onPanDocumentView,
    isPanDocumentUploading = false,
    isPanDocumentViewing = false,
    panDocumentError,
}: ManagePayoutBottomSheetProps) {
    const earnings = useTranslations("earnings");
    const common = useTranslations("common");
    const initialCountry = initialData?.country || INDIA_COUNTRY_CODE;
    const initialMethod = initialCountry !== INDIA_COUNTRY_CODE ? PAYOUT_METHOD.PAYPAL : (initialData?.method || PAYOUT_METHOD.BANK);

    const [formData, setFormData] = useState<ManagePayoutFormData>({
        method: initialMethod,
        country: initialCountry,
        address: initialData?.address || "",
        account_type: initialData?.account_type || "savings",
        beneficiary_name: initialData?.beneficiary_name || "",
        bank_account: initialData?.bank_account || "",
        confirm_bank_account: initialData?.bank_account || "",
        ifsc: initialData?.ifsc || "",
        pan: initialData?.pan || "",
        paypal_email: initialData?.paypal_email || "",
    });

    const [countrySearch, setCountrySearch] = useState("");
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Partial<Record<keyof ManagePayoutFormData, string>>>({});
    const [panDocumentValidationError, setPanDocumentValidationError] = useState<string | null>(null);
    const requiresPan = formData.method === PAYOUT_METHOD.BANK && formData.country === INDIA_COUNTRY_CODE;

    const isOpenRef = React.useRef(isOpen);
    const countryDropdownRef = React.useRef<HTMLDivElement>(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Update form data when the bottom sheet transitions to open
    useEffect(() => {
        if (isOpen && !isOpenRef.current) {
            if (initialData) {
                const initialCountry = initialData.country || INDIA_COUNTRY_CODE;
                const initialMethod = initialCountry !== INDIA_COUNTRY_CODE ? PAYOUT_METHOD.PAYPAL : (initialData.method || PAYOUT_METHOD.BANK);
                setFormData({
                    method: initialMethod,
                    country: initialCountry,
                    address: initialData.address || "",
                    account_type: initialData.account_type || "savings",
                    beneficiary_name: initialData.beneficiary_name || "",
                    bank_account: initialData.bank_account || "",
                    confirm_bank_account: initialData.bank_account || "",
                    ifsc: initialData.ifsc || "",
                    pan: initialData.pan || "",
                    paypal_email: initialData.paypal_email || "",
                });
            }
            setErrors({});
            setPanDocumentValidationError(null);
            setCountrySearch("");
            setIsCountryDropdownOpen(false);
        }
        isOpenRef.current = isOpen;
    }, [isOpen, initialData]);

    useEffect(() => {
        if (!requiresPan || panDocument) {
            setPanDocumentValidationError(null);
        }
    }, [panDocument, requiresPan]);

    const filteredCountries = SUPPORTED_COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const selectedCountry = SUPPORTED_COUNTRIES.find(
        (c) => c.code === formData.country
    );

    const handleInputChange = (
        field: keyof ManagePayoutFormData,
        value: string
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error when user types
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleCountrySelect = (countryCode: string) => {
        setFormData((prev) => ({
            ...prev,
            country: countryCode,
            ...(countryCode !== INDIA_COUNTRY_CODE ? { method: PAYOUT_METHOD.PAYPAL } : {}),
        }));
        setCountrySearch("");
        setIsCountryDropdownOpen(false);
        if (errors.country) {
            setErrors((prev) => ({ ...prev, country: undefined }));
        }
    };

    const validateForm = () => {
        const newErrors: Partial<Record<keyof ManagePayoutFormData, string>> = {};
        let hasPanDocumentError = false;

        if (!formData.country) newErrors.country = earnings("managePayoutErrorCountryRequired");
        if (!formData.address?.trim()) newErrors.address = earnings("managePayoutErrorAddressRequired");

        if (formData.method === PAYOUT_METHOD.BANK) {
            if (!formData.account_type) newErrors.account_type = earnings("managePayoutErrorAccountTypeRequired");
            if (!formData.beneficiary_name?.trim()) newErrors.beneficiary_name = earnings("managePayoutErrorAccountHolderNameRequired");

            // Basic validation for bank account (digits only)
            if (!formData.bank_account?.trim()) {
                newErrors.bank_account = earnings("managePayoutErrorAccountNumberRequired");
            } else if (!/^\d+$/.test(formData.bank_account.trim())) {
                newErrors.bank_account = earnings("managePayoutErrorAccountNumberInvalid");
            }

            // Confirm bank account validation
            if (!formData.confirm_bank_account?.trim()) {
                newErrors.confirm_bank_account = earnings("managePayoutErrorConfirmAccountNumberRequired");
            } else if (formData.bank_account?.trim() !== formData.confirm_bank_account.trim()) {
                newErrors.confirm_bank_account = earnings("managePayoutErrorConfirmAccountNumberMismatch");
            }

            // Basic IFSC validation (alphanumeric only)
            if (!formData.ifsc?.trim()) {
                newErrors.ifsc = earnings("managePayoutErrorIfscRequired");
            } else if (!/^[A-Z0-9]+$/.test(formData.ifsc.trim())) {
                newErrors.ifsc = earnings("managePayoutErrorIfscInvalid");
            }

            // PAN validation for India
            if (requiresPan) {
                if (!formData.pan?.trim()) {
                    newErrors.pan = earnings("managePayoutErrorPanRequired");
                } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan.trim())) {
                    newErrors.pan = earnings("managePayoutErrorPanInvalid");
                }

                if (onPanDocumentUpload && !panDocument) {
                    hasPanDocumentError = true;
                    setPanDocumentValidationError(earnings("managePayoutErrorPanDocumentRequired"));
                } else {
                    setPanDocumentValidationError(null);
                }
            }
        } else if (formData.method === PAYOUT_METHOD.PAYPAL) {
            if (!formData.paypal_email?.trim()) {
                newErrors.paypal_email = earnings("managePayoutErrorPaypalEmailRequired");
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.paypal_email.trim())) {
                newErrors.paypal_email = earnings("managePayoutErrorPaypalEmailInvalid");
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0 && !hasPanDocumentError;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            await onUpdate(formData);
            onClose();
        } catch (error) {
            console.error("Failed to update payout details:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            className="bg-[#fffaef] px-4 pb-6 pt-3 max-h-[85vh]"
            showDragHandle
        >
            <div className="space-y-6 text-[#344054]">
                {/* Header */}
                <div className="space-y-1">
                    <p className="font-plus-jakarta-sans text-base font-semibold">{earnings("managePayoutTitle")}</p>
                    <p className="text-sm text-[#475467]">
                        {earnings("managePayoutSubtitle")}
                    </p>
                </div>

                <div className="space-y-5 overflow-y-auto max-h-[55vh] pr-1">
                    {/* Country Selector */}
                    <div className="space-y-2">
                        <label htmlFor="country-search" className="text-sm font-medium text-[#667085]">
                            {earnings("managePayoutCountryLabel")} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative" ref={countryDropdownRef}>
                            <Input
                                id="country-search"
                                startIcon={
                                    <svg
                                        className="w-4 h-4 text-[#667085]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                }
                                endIcon={
                                    <svg
                                        className={cn(
                                            "w-4 h-4 text-[#667085] transition-transform duration-200 pointer-events-none",
                                            isCountryDropdownOpen && "rotate-180"
                                        )}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                }
                                placeholder={selectedCountry?.name || earnings("managePayoutCountrySearchPlaceholder")}
                                value={countrySearch}
                                onChange={(e) => {
                                    setCountrySearch(e.target.value);
                                    if (!isCountryDropdownOpen) setIsCountryDropdownOpen(true);
                                }}
                                onClick={() => {
                                    setIsCountryDropdownOpen((prev) => !prev);
                                }}
                                className="h-[46px] cursor-pointer placeholder:text-[#344054] placeholder:opacity-100"
                            />
                            {isCountryDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-black rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredCountries.length > 0 ? (
                                        filteredCountries.map((country) => (
                                            <button
                                                key={country.code}
                                                type="button"
                                                onClick={() => handleCountrySelect(country.code)}
                                                className={cn(
                                                    "w-full px-3 py-2 text-left text-sm text-[#344054] hover:bg-[#F9FAFB] flex items-center justify-between",
                                                    formData.country === country.code && "bg-[#F2F4F7] font-medium"
                                                )}
                                            >
                                                <span>{country.name}</span>
                                                {formData.country === country.code && (
                                                    <span className="text-xs text-[#0D41E1]">✓</span>
                                                )}
                                            </button>
                                        ))
                                    ) : (
                                         <div className="px-3 py-2 text-sm text-gray-500">
                                             {earnings("managePayoutCountryNoResults")}
                                         </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {errors.country && <p className="text-xs text-red-500">{errors.country}</p>}
                        <p className="text-xs text-[#667085]">
                            {earnings("managePayoutCountrySupportText")}{" "}
                            <a href="#" className="text-[#0D41E1] hover:underline" onClick={(e) => e.preventDefault()}>
                                {earnings("managePayoutCountrySupportLinkLabel")}
                            </a>
                            .
                        </p>
                    </div>

                    {/* Payout Method Toggle */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-[#667085]">
                            {earnings("managePayoutMethodLabel")} <span className="text-red-500">*</span>
                        </label>
                        {formData.country === INDIA_COUNTRY_CODE ? (
                            <div className="grid grid-cols-2 gap-2 p-1 bg-[#F2F4F7] rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => handleInputChange("method", PAYOUT_METHOD.BANK)}
                                    className={cn(
                                        "py-2 text-sm font-medium rounded-md transition-all duration-200",
                                        formData.method === PAYOUT_METHOD.BANK
                                            ? "bg-white text-[#344054] shadow-sm"
                                            : "text-[#667085] hover:text-[#344054]"
                                    )}
                                >
                                    {earnings("managePayoutBankTransferOption")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange("method", PAYOUT_METHOD.PAYPAL)}
                                    className={cn(
                                        "py-2 text-sm font-medium rounded-md transition-all duration-200",
                                        formData.method === PAYOUT_METHOD.PAYPAL
                                            ? "bg-white text-[#344054] shadow-sm"
                                            : "text-[#667085] hover:text-[#344054]"
                                    )}
                                >
                                    {earnings("managePayoutPaypalOption")}
                                </button>
                            </div>
                        ) : (
                            <div className="p-1 bg-[#F2F4F7] rounded-lg">
                                <button
                                    type="button"
                                    disabled
                                    className="w-full py-2 text-sm font-medium rounded-md bg-white text-[#344054] shadow-sm cursor-default"
                                >
                                    {earnings("managePayoutPaypalOption")}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Dynamic Fields based on method */}
                    {
                        formData.method === PAYOUT_METHOD.BANK ? (
                            <div className="space-y-5">
                                {/* Account Type */}
                                <div className="space-y-2">
                                    <label htmlFor="account-type" className="text-sm font-medium text-[#667085]">
                                        {earnings("managePayoutAccountTypeLabel")} <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={formData.account_type}
                                        onValueChange={(value) => handleInputChange("account_type", value)}
                                    >
                                        <SelectTrigger id="account-type" className="h-[46px]">
                                            <SelectValue placeholder={earnings("managePayoutAccountTypePlaceholder")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="savings">{earnings("managePayoutSavingsOption")}</SelectItem>
                                            <SelectItem value="current">{earnings("managePayoutCurrentOption")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {errors.account_type && <p className="text-xs text-red-500">{errors.account_type}</p>}
                                </div>

                                {/* Account Holder Name */}
                                <div className="space-y-2">
                                    <label htmlFor="beneficiary-name" className="text-sm font-medium text-[#667085]">
                                        {earnings("managePayoutAccountHolderNameLabel")} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="beneficiary-name"
                                        placeholder={earnings("managePayoutAccountHolderNamePlaceholder")}
                                        value={formData.beneficiary_name}
                                        onChange={(e) => handleInputChange("beneficiary_name", e.target.value)}
                                        className="h-[46px]"
                                    />
                                    <p className="text-xs text-[#667085]">
                                        Must match the official name registered on your bank account statement.
                                    </p>
                                    {errors.beneficiary_name && <p className="text-xs text-red-500">{errors.beneficiary_name}</p>}
                                </div>

                                {/* Account Number - Password Style */}
                                <div className="space-y-2">
                                    <label htmlFor="bank-account" className="text-sm font-medium text-[#667085]">
                                        {earnings("managePayoutAccountNumberLabel")} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="bank-account"
                                        type="password"
                                        placeholder={earnings("managePayoutAccountNumberPlaceholder")}
                                        value={formData.bank_account}
                                        onChange={(e) => handleInputChange("bank_account", e.target.value)}
                                        className="h-[46px] font-[Inter,sans-serif]"
                                        style={{ fontVariantNumeric: "slashed-zero" }}
                                    />
                                    {errors.bank_account && <p className="text-xs text-red-500">{errors.bank_account}</p>}
                                </div>

                                {/* Confirm Account Number - Text Style */}
                                <div className="space-y-2">
                                    <label htmlFor="confirm-bank-account" className="text-sm font-medium text-[#667085]">
                                        {earnings("managePayoutConfirmAccountNumberLabel")} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="confirm-bank-account"
                                        type="text"
                                        placeholder={earnings("managePayoutConfirmAccountNumberPlaceholder")}
                                        value={formData.confirm_bank_account}
                                        onChange={(e) => handleInputChange("confirm_bank_account", e.target.value)}
                                        className="h-[46px] font-[Inter,sans-serif]"
                                        style={{ fontVariantNumeric: "slashed-zero" }}
                                    />
                                    {errors.confirm_bank_account && <p className="text-xs text-red-500">{errors.confirm_bank_account}</p>}
                                </div>

                                {/* IFSC Code - Text Style */}
                                <div className="space-y-2">
                                    <label htmlFor="ifsc" className="text-sm font-medium text-[#667085]">
                                        {earnings("managePayoutIfscLabel")} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="ifsc"
                                        type="text"
                                        placeholder={earnings("managePayoutIfscPlaceholder")}
                                        value={formData.ifsc}
                                        onChange={(e) => handleInputChange("ifsc", e.target.value.toUpperCase())}
                                        className="h-[46px] font-[Inter,sans-serif]"
                                        style={{ fontVariantNumeric: "slashed-zero" }}
                                    />
                                    {errors.ifsc && <p className="text-xs text-red-500">{errors.ifsc}</p>}
                                </div>

                                {/* PAN Number for India */}
                                {requiresPan && (
                                    <div className="space-y-2">
                                        <label htmlFor="pan" className="text-sm font-medium text-[#667085]">
                                            {earnings("managePayoutPanLabel")} <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            id="pan"
                                            type="text"
                                            placeholder={earnings("managePayoutPanPlaceholder")}
                                            value={formData.pan}
                                            onChange={(e) => handleInputChange("pan", e.target.value.toUpperCase())}
                                            className="h-[46px] font-[Inter,sans-serif]"
                                            style={{ fontVariantNumeric: "slashed-zero" }}
                                        />
                                        {errors.pan && <p className="text-xs text-red-500">{errors.pan}</p>}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-5">
                                {/* PayPal Email */}
                                <div className="space-y-2">
                                    <label htmlFor="paypal-email" className="text-sm font-medium text-[#667085]">
                                        {earnings("managePayoutPaypalEmailLabel")} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="paypal-email"
                                        type="email"
                                        placeholder={earnings("managePayoutPaypalEmailPlaceholder")}
                                        value={formData.paypal_email}
                                        onChange={(e) => handleInputChange("paypal_email", e.target.value)}
                                        className="h-[46px]"
                                    />
                                    {errors.paypal_email && <p className="text-xs text-red-500">{errors.paypal_email}</p>}
                                    <p className="text-xs text-[#667085]">
                                        Please use your registered PayPal email address.
                                    </p>
                                </div>
                            </div>
                        )
                    }

                    {/* Address */}
                    < div className="space-y-2" >
                        <label htmlFor="address" className="text-sm font-medium text-[#667085]">
                            {earnings("managePayoutAddressLabel")} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="address"
                            rows={3}
                            placeholder={earnings("managePayoutAddressPlaceholder")}
                            value={formData.address}
                            onChange={(e) => handleInputChange("address", e.target.value)}
                            className="flex w-full bg-white text-sm text-gray-900 placeholder:text-gray-400 border border-black rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none"
                        />
                        {errors.address && <p className="text-xs text-red-500">{errors.address}</p>}
                    </div >

                    {requiresPan && onPanDocumentUpload ? (
                        <DocumentUploadField
                            id="pan-document"
                            title={earnings("managePayoutPanDocumentTitle")}
                            description={earnings("managePayoutPanDocumentDescription")}
                            document={panDocument}
                            accept={PAN_DOCUMENT_ACCEPT}
                            maxSizeMb={PAN_DOCUMENT_MAX_SIZE_MB}
                            uploadLabel={earnings("managePayoutPanDocumentUploadLabel")}
                            replaceLabel={earnings("managePayoutPanDocumentReplaceLabel")}
                            viewLabel={earnings("managePayoutPanDocumentViewLabel")}
                            uploadingLabel={earnings("managePayoutPanDocumentUploadingLabel")}
                            viewingLabel={earnings("managePayoutPanDocumentViewingLabel")}
                            fileTooLargeLabel={earnings("managePayoutErrorPanDocumentFileTooLarge", {
                                maxSizeMb: PAN_DOCUMENT_MAX_SIZE_MB,
                            })}
                            statusLabels={{
                                pendingReview: earnings("managePayoutPanDocumentStatusPendingReview"),
                                verified: earnings("managePayoutPanDocumentStatusVerified"),
                                rejected: earnings("managePayoutPanDocumentStatusRejected"),
                            }}
                            supportedText={earnings("managePayoutPanDocumentSupportedText", {
                                maxSizeMb: PAN_DOCUMENT_MAX_SIZE_MB,
                            })}
                            isUploading={isPanDocumentUploading}
                            isViewing={isPanDocumentViewing}
                            error={panDocumentValidationError || panDocumentError}
                            onUpload={onPanDocumentUpload}
                            onView={onPanDocumentView}
                        />
                    ) : null}
                </div >

                {/* Footer Buttons */}
                < div className="flex gap-3 pt-2" >
                    <Button
                        variant="outline"
                        className="flex-1 rounded-lg"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {common("close")}
                    </Button>
                    <Button
                        className="flex-1 rounded-lg"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoading}
                    >
                        {isSubmitting ? earnings("managePayoutUpdating") : common("update")}
                    </Button>
                </div >
            </div >
        </BottomSheet >
    );
}
